import React, { useEffect } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import { getAllPlans, getCurrentSubscription } from "store/actions/subscriptionAction"
import { ROUTES } from "constants/routes"
import "./billing.scss"

const Plans = () => {
  const { t } = useTranslation()
  document.title = t("Subscription Plans ")

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { plansLoading, plans, currentSubscription } = useSelector(
    state => state.Subscription
  )

  useEffect(() => {
    dispatch(getAllPlans())
    dispatch(getCurrentSubscription())
  }, [dispatch])

  const handleSelectPlan = plan => {
    navigate(`${ROUTES.BILLING_CHECKOUT}?planId=${plan._id}`)
  }

  const isCurrentPlan = planId =>
    currentSubscription?.plan?._id === planId ||
    currentSubscription?.planId === planId ||
    currentSubscription?.planId?._id === planId

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <h4 className="mb-1">{t("Subscription Plans")}</h4>
          <p className="text-muted mb-4">
            {t("Choose the plan that best fits your needs.")}
          </p>

          {plansLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-5 text-muted">
              {t("No plans available at the moment.")}
            </div>
          ) : (
            <Row className="justify-content-center">
              {plans.map((plan, idx) => (
                <Col key={plan._id || idx} xl={3} lg={4} md={6} className="mb-4">
                  <Card
                    className={`plan-card h-100 ${
                      isCurrentPlan(plan._id) ? "active-plan" : ""
                    } ${plan.recommended ? "recommended" : ""}`}
                  >
                    <CardBody className="d-flex flex-column">
                      {plan.recommended && (
                        <Badge color="success" className="mb-2 align-self-start">
                          {t("Recommended")}
                        </Badge>
                      )}
                      {isCurrentPlan(plan._id) && (
                        <Badge color="primary" className="mb-2 align-self-start">
                          {t("Current Plan")}
                        </Badge>
                      )}

                      <h5 className="fw-bold mb-1">{plan.name}</h5>
                      <p className="text-muted small mb-3">{plan.description}</p>

                      <div className="mb-3">
                        <span className="fs-2 fw-bold">
                          {plan.currency || "SAR"}{" "}
                          {Number(plan.price || 0).toFixed(2)}
                        </span>
                        <span className="text-muted">
                          {" / "}
                          {plan.billingCycle === "yearly"
                            ? t("year")
                            : t("month")}
                        </span>
                      </div>

                      {/* Included modules */}
                      {Array.isArray(plan.modules) && plan.modules.length > 0 && (
                        <div className="mb-3">
                          <p className="text-muted small mb-1 fw-medium">
                            {t("Included Modules")}
                          </p>
                          <ul className="plan-feature-list">
                            {plan.modules.map((mod, mIdx) => {
                              const id = typeof mod === "string" ? mod : mod.id
                              const label =
                                typeof mod === "string"
                                  ? mod
                                  : mod.label || mod.id || ""
                              const isDefault =
                                typeof mod === "object" && mod.isDefault
                              return (
                                <li key={id || mIdx}>
                                  <i className="bx bx-check text-success" />
                                  {t(label)}
                                  {isDefault && (
                                    <Badge
                                      color="light"
                                      className="ms-1 text-muted"
                                      style={{ fontSize: "0.65rem" }}
                                    >
                                      {t("default")}
                                    </Badge>
                                  )}
                                </li>
                              )
                            })}
                          </ul>
                        </div>
                      )}

                      {/* Legacy features list (backward compat) */}
                      {Array.isArray(plan.features) &&
                        plan.features.length > 0 &&
                        !(Array.isArray(plan.modules) && plan.modules.length > 0) && (
                          <ul className="plan-feature-list flex-grow-1 mb-3">
                            {plan.features.map((feature, fIdx) => (
                              <li key={fIdx}>
                                <i className="bx bx-check text-success" />
                                {typeof feature === "string"
                                  ? feature
                                  : feature.label || feature.name || ""}
                              </li>
                            ))}
                          </ul>
                        )}

                      {/* Add-ons pricing */}
                      {Array.isArray(plan.addOns) && plan.addOns.length > 0 && (
                        <div className="mb-3">
                          <p className="text-muted small mb-1 fw-medium">
                            {t("Available Add-ons")}
                          </p>
                          <ul className="plan-feature-list">
                            {plan.addOns.map((addon, aIdx) => (
                              <li key={aIdx} className="d-flex justify-content-between">
                                <span>
                                  <i className="bx bx-plus-circle text-primary me-1" />
                                  {addon.label || addon.name || ""}
                                </span>
                                {addon.price !== undefined && (
                                  <span className="text-muted small">
                                    {plan.currency || "SAR"}{" "}
                                    {Number(addon.price).toFixed(2)}
                                    {addon.unit ? ` ${addon.unit}` : ""}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <Button
                        color={isCurrentPlan(plan._id) ? "secondary" : "primary"}
                        block
                        className="mt-auto"
                        disabled={isCurrentPlan(plan._id)}
                        onClick={() => handleSelectPlan(plan)}
                      >
                        {isCurrentPlan(plan._id)
                          ? t("Current Plan")
                          : t("Select Plan")}
                      </Button>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

export default Plans
